import { CheckCircleOutlined } from '@ant-design/icons';
import { Tag } from 'antd';
import { TagProps } from 'antd/es/tag';
import dayjs, { Dayjs } from 'dayjs';
import api from '../api';
import { useRequest } from '../api/hooks';
import { GMTInfo } from './common';

export const AppointmentEndpoint = `/appointment`;

export enum AppointmentChannel {
  Placeholder = 0,
  Weapp = 1,
  Admin = 100,
}

export const AppointmentChannelDesc = {
  [AppointmentChannel.Placeholder]: <Tag>POS占位</Tag>,
  [AppointmentChannel.Weapp]: <Tag color="green">小程序</Tag>,
  [AppointmentChannel.Admin]: <Tag color="blue">POS</Tag>,
};

export enum AppointmentStatus {
  FAILED = -2,
  CANCELED = -1,
  CREATED = 0,
  CONFIRMED = 2,
  CHECKIN = 3,
  COMPLETE = 4,
}

export const AppointmentStatusMap = {
  [AppointmentStatus.FAILED]: {
    desc: '预约失败',
    type: '',
    icon: undefined,
  },
  [AppointmentStatus.CANCELED]: {
    desc: '已取消',
    type: '',
    icon: undefined,
  },
  [AppointmentStatus.CREATED]: {
    desc: '待确认',
    type: 'processing',
    icon: undefined,
  },
  [AppointmentStatus.CONFIRMED]: {
    desc: '预约成功',
    type: 'warning',
    icon: undefined,
  },
  [AppointmentStatus.CHECKIN]: {
    desc: '已到店',
    type: 'processing',
    icon: <CheckCircleOutlined />,
  },
  [AppointmentStatus.COMPLETE]: {
    desc: '已完成',
    type: 'success',
    icon: <CheckCircleOutlined />,
  },
};

export const getAppointmentStatusTag = (status: AppointmentStatus, tagProps: TagProps = {}) => {
  const props = {
    color: AppointmentStatusMap[status].type,
    children: AppointmentStatusMap[status].desc,
    icon: AppointmentStatusMap[status].icon,
  };
  return <Tag {...props} {...tagProps} />;
};

export const calcAppointmentDuration = (record: Appointment) => {
  return Math.ceil(dayjs(record.endTime).diff(dayjs(record.startTime), 'seconds') / 60);
};

/** 预约信息 */
export interface Appointment extends GMTInfo {
  id?: number;
  status: AppointmentStatus;
  channel: keyof typeof AppointmentChannelDesc;
  count: number;
  startTime: string;
  endTime: string;
  checkinTime: string;
  memo: string;
  phone: string;
  orderSn: string;
  userId: number;
  goodsId: number;
}

export interface CalendarInfo {
  /* 最多可预订天数 */
  maxBookableDays: number;
  /* 最近可预订天数, 0: 最早可预订今天， 1： 最早可预订明天 */
  latestBookableDays: number;
  /* 可预约最小单位(分钟) */
  minBooksLength: number;
  /* 单次服务默认时长(分钟) */
  defaultServiceDuration: number;
  /* 单位时间最多可预约 */
  maxStockPerSlot: number;
  /*相较当前，至少提前预约时间(分钟) */
  earliestBookingTimeFromNow: number;
  /*一天中最早可预订时间 */
  earliestBookingTime: string;
  /*一天中最晚可预订时间 */
  latestBookingTime: string;
  now: Dayjs;
  bookedMap: { [k: number]: { total: number } };
  conflictText: string;
  showLastSlotTip: boolean;
  slotInfoMap: {
    [k: number]: { stock: number; invalidReason: number; tip?: string };
  };
}

export const viewCalendar = async (goodsIdList?: number[]) => {
  const data = (await api.post({
    url: `${AppointmentEndpoint}/calendar`,
    data: { goodsIdList },
  })) as CalendarInfo;
  data.now = dayjs(data.now);
  return data;
};

export const book = async (bookInfo: Appointment): Promise<Appointment> => {
  return await api.post({ url: `${AppointmentEndpoint}/book`, data: bookInfo });
};

export const bookPlaceholder = async (bookInfo: Appointment): Promise<Appointment> => {
  return await api.post({ url: `${AppointmentEndpoint}/book/placeholder`, data: bookInfo });
};

export const useBook = () => {
  const { send, ...others } = useRequest<Appointment>(
    {
      method: 'post',
      url: `${AppointmentEndpoint}/book`,
    },
    {
      manual: true,
    }
  );

  const post = (data: Appointment) => {
    return send({
      data,
    });
  };

  return {
    send: post,
    ...others,
  };
};

export const getInvalidReason = (code: number) => {
  if (code === 1) {
    return '已约满';
  } else if (code === 2) {
    return '预留时段';
  }
  return undefined;
};

export const addSpecialSlots = async ({ start, end, stock }: { start: string; end: string; stock: number }) => {
  return api.post({
    url: `${AppointmentEndpoint}/specialSlots`,
    data: {
      start,
      end,
      stock,
    },
  });
};

export const deleteSpecialSlots = async ({ start }: { start: string }) => {
  return api.delete({
    url: `${AppointmentEndpoint}/specialSlots`,
    data: {
      start,
    },
  });
};

export const updateStatus = async ({
  appointment,
  newStatus,
}: {
  appointment: Appointment;
  newStatus: AppointmentStatus;
}) => {
  return api.post({
    url: `${AppointmentEndpoint}/status`,
    data: {
      ...appointment,
      status: newStatus,
    },
  });
};

export const AppointmentHelper = {
  canCancel: (appointment: Appointment) => {
    if (
      appointment &&
      appointment.status !== AppointmentStatus.COMPLETE &&
      appointment.status !== AppointmentStatus.CANCELED
    ) {
      return true;
    }
    return false;
  },
  canCheckIn: (appointment: Appointment) => {
    if (appointment && appointment.status === AppointmentStatus.CONFIRMED) {
      return true;
    }
    return false;
  },
  canComplete: (appointment: Appointment) => {
    if (appointment && appointment.status === AppointmentStatus.CHECKIN) {
      return true;
    }
    return false;
  },
};
